'use client';

import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Search, ChevronDown, LayoutGrid, List, GalleryVertical, ShoppingBag, ZoomIn, ZoomOut, Minus, Plus, LayoutPanelTop } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useDebounce } from '../../lib/hooks/use-debounce';
import { motion, AnimatePresence } from 'framer-motion';
import { useStoreUI, LayoutMode } from '../../lib/store/use-store-ui';
import { useCartItemCount, useCartStore } from '../../lib/store/cart-store';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { label: 'All', value: '' },
  { label: 'Digital', value: 'digital' },
  { label: 'Artwork', value: 'artwork' },
  { label: 'Print', value: 'print' },
  { label: 'Sticker', value: 'sticker' },
  { label: 'Merch', value: 'merch' },
  { label: 'Craft', value: 'craft' },
];

const COLLECTIONS = [
  { label: 'All Collections', value: '' },
  { label: 'Organic Imaginary', value: 'organic-imaginary' },
  { label: 'Animal Kingdom', value: 'animal-kingdom' },
  { label: 'Mushland', value: 'mushland' },
  { label: 'Seven Elements', value: 'seven-elements' },
  { label: 'Bitcoin', value: 'bitcoin' },
  { label: 'Dope Crew', value: 'dope-crew' },
  { label: 'Words', value: 'words' },
  { label: 'Fruits & Veggies', value: 'fruits-veggies' },
  { label: 'Flowers', value: 'flowers' },
  { label: 'Kings & Queens', value: 'kings-queens' },
  { label: 'Polygonal HD', value: 'polygonal-hd' },
  { label: 'Reliquias', value: 'reliquias' },
  { label: 'Bituaya', value: 'bituaya' },
  { label: 'Landscapes', value: 'landscapes' },
  { label: 'Exiliado', value: 'exiliado' },
  { label: 'Client Work', value: 'client-work' },
];

const LAYOUTS = [
  { id: 'masonry', icon: GalleryVertical, label: 'Masonry' },
  { id: 'list', icon: List, label: 'List' },
  { id: 'grid', icon: LayoutGrid, label: 'Grid' },
] as const;

export default function StoreFilters() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { layoutMode, setLayoutMode, zoomLevel, setZoomLevel } = useStoreUI();
  const cartItemCount = useCartItemCount();
  const setDrawerOpen = useCartStore((s) => s.setDrawerOpen);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Search state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Category state (Optimistic UI)
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    searchParams.get('category')?.split(',').filter(Boolean) || []
  );
  const debouncedCategories = useDebounce(selectedCategories, 300);

  const currentCollection = searchParams.get('collection') || '';

  // Refs to track last URL values to prevent feedback loops with local state debouncing
  const lastUrlCategoryRef = useRef(searchParams.get('category') || '');
  const lastUrlSearchRef = useRef(searchParams.get('search') || '');

  // Update search param when debounced search changes
  useEffect(() => {
    const currentSearch = searchParams.get('search') || '';
    if (debouncedSearch === currentSearch) return;

    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    lastUrlSearchRef.current = debouncedSearch;
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }, [debouncedSearch, pathname, router, searchParams]);

  // Update category param when debounced categories change
  useEffect(() => {
    const currentCategoryString = searchParams.get('category') || '';
    const nextCategoryString = debouncedCategories.join(',');

    if (currentCategoryString === nextCategoryString) return;

    const params = new URLSearchParams(searchParams);
    if (nextCategoryString) {
      params.set('category', nextCategoryString);
    } else {
      params.delete('category');
    }
    params.set('page', '1');
    lastUrlCategoryRef.current = nextCategoryString;
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }, [debouncedCategories, pathname, router, searchParams]);

  // Sync internal category state if URL changes externally (e.g. back button)
  useEffect(() => {
    const urlCategory = searchParams.get('category') || '';
    if (urlCategory !== lastUrlCategoryRef.current) {
      lastUrlCategoryRef.current = urlCategory;
      setSelectedCategories(urlCategory.split(',').filter(Boolean));
    }
  }, [searchParams]);

  // Sync internal search state if URL changes externally (e.g. back button)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch !== lastUrlSearchRef.current) {
      lastUrlSearchRef.current = urlSearch;
      setSearchTerm(urlSearch);
    }
  }, [searchParams]);

  const toggleCategory = (value: string) => {
    if (value === '') {
      setSelectedCategories([]);
      return;
    }

    setSelectedCategories((prev) => {
      if (prev.includes(value)) {
        const next = prev.filter((c) => c !== value);
        return next;
      }
      return [...prev, value];
    });
  };

  const handleCollectionChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('collection', value);
    } else {
      params.delete('collection');
    }
    params.set('page', '1');
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  return (
    <div className="space-y-4 w-full">
      {/* Search, Collection, Layout */}
      <div className="flex flex-col md:flex-row items-center gap-4 w-full">
        {/* Search Input */}
        <div className="relative flex-1 w-full min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search our universe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg py-1.5 pl-10 pr-4 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all placeholder:text-muted-foreground/50"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          {/* Collection Dropdown */}
          <div className="relative min-w-[160px] flex-1 md:w-48">
            <select
              value={currentCollection}
              onChange={(e) => handleCollectionChange(e.target.value)}
              className="w-full appearance-none bg-white/5 border border-white/10 rounded-lg py-1.5 pl-3 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all cursor-pointer"
            >
              {COLLECTIONS.map((col) => (
                <option key={col.value} value={col.value} className="bg-gray-950 text-white">
                  {col.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>

          {/* Layout Toggles */}
          <div className="flex items-center bg-white/5 border border-white/10 rounded-xl p-0.5 shadow-inner">
            {[
              { id: 'masonry', icon: GalleryVertical, label: 'Masonry' },
              { id: 'list', icon: List, label: 'List' },
              { id: 'grid', icon: LayoutGrid, label: 'Grid' },
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => setLayoutMode(mode.id as any)}
                className={cn(
                  "relative p-1.5 rounded-md transition-all duration-300",
                  layoutMode === mode.id ? "text-cyan-400" : "text-muted-foreground hover:text-white"
                )}
                title={mode.label}
              >
                {layoutMode === mode.id && (
                  <motion.div
                    layoutId="layout-active"
                    className="absolute inset-0 bg-cyan-500/10 rounded-md border border-cyan-500/20"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <mode.icon className="h-3.5 w-3.5 relative z-10" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row: Category Pills & Zoom */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground/40 mr-2">Categories:</span>
          {CATEGORIES.map((cat) => {
            const isActive = cat.value === ''
              ? selectedCategories.length === 0
              : selectedCategories.includes(cat.value);

            return (
              <button
                key={cat.value}
                onClick={() => toggleCategory(cat.value)}
                className={cn(
                  "relative px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 border",
                  isActive
                    ? "border-cyan-500/50 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.15)] bg-cyan-500/5"
                    : "border-white/5 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white"
                )}
              >
                {isActive && (
                  <motion.div
                    layoutId="active-pill"
                    className="absolute inset-0 rounded-full bg-cyan-500/5"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5 shadow-inner">
          {[-1, 0, 1].map((level) => (
            <button
              key={level}
              onClick={() => setZoomLevel(level)}
              className={cn(
                "relative p-1.5 rounded-md transition-all duration-300",
                zoomLevel === level ? "text-cyan-400" : "text-muted-foreground hover:text-white"
              )}
              title={level === -1 ? "Compact" : level === 1 ? "Large" : "Standard"}
            >
              {zoomLevel === level && (
                <motion.div
                  layoutId="zoom-active"
                  className="absolute inset-0 bg-cyan-500/10 rounded-md border border-cyan-500/20"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              {level === -1 ? <ZoomOut className="h-3.5 w-3.5 relative z-10" /> :
                level === 1 ? <ZoomIn className="h-3.5 w-3.5 relative z-10" /> :
                  <div className="w-3.5 h-3.5 flex items-center justify-center relative z-10 font-black text-[7px]">1x</div>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
